import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { 
  Users, Send, Mail, Plus, Search, Filter, Eye, Pencil, Trash2, 
  ChevronRight, Clock, CheckCircle2, AlertCircle, X, ExternalLink 
} from "lucide-react";

const H = { fontFamily: "'Cabinet Grotesk', sans-serif" };

const SKIP_LABELS = {
  no_marketing_consent: "patient has not accepted marketing consent",
  unsubscribed: "patient has unsubscribed",
  missing_email: "no email address on file",
  invalid_email: "email address looks invalid",
};

const CAMPAIGN_STATUS = {
  draft: { label: "Draft", badge: "bg-amber-50 text-amber-600", iconBg: "bg-amber-50" },
  sending: { label: "Sending", badge: "bg-blue-50 text-blue-600", iconBg: "bg-blue-50" },
  sent: { label: "Sent", badge: "bg-emerald-50 text-emerald-600", iconBg: "bg-emerald-50" },
  partially_sent: { label: "Partially sent", badge: "bg-amber-100 text-amber-700", iconBg: "bg-amber-50" },
  failed: { label: "Failed", badge: "bg-red-50 text-red-600", iconBg: "bg-red-50" },
  skipped: { label: "No eligible recipients", badge: "bg-[#FAF9F6] text-[#757675]", iconBg: "bg-[#FAF9F6]" },
};

export default function MarketingPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState("audience");
  const [loading, setLoading] = useState(true);
  
  // Audience state
  const [subscribers, setSubscribers] = useState([]);
  const [subscriberCounts, setSubscriberCounts] = useState({ total: 0, subscribed: 0, unsubscribed: 0, pending: 0 });
  const [audienceFilter, setAudienceFilter] = useState("");
  const [audienceSearch, setAudienceSearch] = useState("");
  
  // Campaigns state
  const [campaigns, setCampaigns] = useState([]);
  const [showNewCampaign, setShowNewCampaign] = useState(false);
  const [newCampaignForm, setNewCampaignForm] = useState({ title: "", subject: "", body: "" });
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [previewCampaign, setPreviewCampaign] = useState(null);
  const [sendConfirm, setSendConfirm] = useState(null);
  const [sendResult, setSendResult] = useState(null);
  const [sentHistory, setSentHistory] = useState([]);
  const [sending, setSending] = useState(false);
  
  // Stats state
  const [stats, setStats] = useState({ total_emails_sent: 0, total_campaigns_sent: 0, recent_campaigns: [] });
  
  const loadAudience = useCallback(async () => {
    try {
      const query = new URLSearchParams();
      if (audienceFilter) query.append("status", audienceFilter);
      if (audienceSearch) query.append("search", audienceSearch);
      const { data } = await api.get(`/marketing/subscribers?${query.toString()}`);
      setSubscribers(data.subscribers);
      setSubscriberCounts(data.counts);
    } catch {}
    setLoading(false);
  }, [audienceFilter, audienceSearch]);
  
  const loadCampaigns = useCallback(async () => {
    try {
      const { data } = await api.get("/marketing/campaigns");
      setCampaigns(data.campaigns);
    } catch {}
  }, []);
  
  const loadStats = useCallback(async () => {
    try {
      const { data } = await api.get("/marketing/sent/stats");
      setStats(data);
    } catch {}
    try {
      const { data } = await api.get("/marketing/sent?limit=200");
      setSentHistory(data.sent || []);
    } catch {}
  }, []);
  
  useEffect(() => { loadAudience(); loadCampaigns(); loadStats(); }, [loadAudience, loadCampaigns, loadStats]);

  const createCampaign = async () => {
    if (!newCampaignForm.title.trim() || !newCampaignForm.subject.trim()) return;
    try {
      await api.post("/marketing/campaigns", newCampaignForm);
      setShowNewCampaign(false);
      setNewCampaignForm({ title: "", subject: "", body: "" });
      loadCampaigns();
    } catch (e) { alert(e.response?.data?.detail || "Error creating campaign"); }
  };

  const updateCampaign = async () => {
    if (!editingCampaign) return;
    try {
      await api.put(`/marketing/campaigns/${editingCampaign.id}`, {
        title: editingCampaign.title,
        subject: editingCampaign.subject,
        body: editingCampaign.body,
      });
      setEditingCampaign(null);
      loadCampaigns();
    } catch (e) { alert(e.response?.data?.detail || "Error updating campaign"); }
  };

  const deleteCampaign = async (id) => {
    if (!window.confirm("Delete this campaign draft?")) return;
    try {
      await api.delete(`/marketing/campaigns/${id}`);
      loadCampaigns();
    } catch (e) { alert(e.response?.data?.detail || "Error deleting campaign"); }
  };

  const previewAndSend = async (campaign) => {
    try {
      const { data } = await api.post(`/marketing/campaigns/${campaign.id}/preview`);
      setPreviewCampaign(data);
    } catch (e) { alert(e.response?.data?.detail || "Error loading preview"); }
  };

  const confirmSend = async () => {
    if (!sendConfirm) return;
    setSending(true);
    try {
      const { data } = await api.post(`/marketing/campaigns/${sendConfirm.campaign.id}/send`, { confirm: true });
      setSendResult({
        status: data.campaign_status,
        message: data.message,
        sent: data.sent_count,
        failed: data.failed_count,
        skipped: data.skipped_count,
        queued: data.queued_count,
      });
      setSendConfirm(null);
      setPreviewCampaign(null);
      loadCampaigns();
      loadStats();
    } catch (e) {
      setSendResult({
        status: "failed",
        message: e.response?.data?.detail || "Campaign could not be sent. No emails were delivered.",
      });
      setSendConfirm(null);
    }
    setSending(false);
  };

  const retryCampaign = async (campaignId) => {
    setSending(true);
    try {
      const { data } = await api.post(`/marketing/campaigns/${campaignId}/retry`);
      setSendResult({
        status: data.campaign_status,
        message: data.message,
        sent: data.sent_count,
        failed: data.failed_count,
        skipped: data.skipped_count,
        queued: data.queued_count,
      });
      loadCampaigns();
      loadStats();
    } catch (e) {
      setSendResult({ status: "failed", message: e.response?.data?.detail || "Retry failed" });
    }
    setSending(false);
  };

  const updateSubscriberStatus = async (subscriberId, status) => {
    try {
      await api.put(`/marketing/subscribers/${subscriberId}`, { status });
      loadAudience();
    } catch (e) { alert(e.response?.data?.detail || "Error updating subscriber"); }
  };

  const tabs = [
    { key: "audience", label: "Audience", icon: Users, count: subscriberCounts.subscribed },
    { key: "campaigns", label: "Campaigns", icon: Mail, count: campaigns.filter(c => c.status === "draft").length },
    { key: "sent", label: "Sent history", icon: Send, count: stats.total_campaigns_sent },
  ];

  return (
    <div className="h-full flex flex-col" data-testid="marketing-page">
      {/* Header */}
      <div className="border-b border-[#1A1B1A]/[0.06] bg-white px-6 py-4 flex items-center justify-between flex-shrink-0">
        <div>
          <h1 className="text-xl font-medium text-[#1A1B1A]" style={H}>Marketing</h1>
          <p className="text-xs text-[#757675]">Consent-driven newsletters and patient communications</p>
        </div>
        {tab === "campaigns" && (
          <button onClick={() => setShowNewCampaign(true)} data-testid="new-campaign-btn"
            className="bg-[#205B5A] text-white px-4 py-2 rounded-full text-xs font-medium hover:bg-[#174544] flex items-center gap-1.5">
            <Plus className="w-3.5 h-3.5" /> New campaign
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="border-b border-[#1A1B1A]/[0.06] bg-white px-6">
        <div className="flex gap-6">
          {tabs.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`tab-${t.key}`}
              className={`py-3 border-b-2 text-xs font-medium flex items-center gap-2 transition-colors ${
                tab === t.key ? "border-[#205B5A] text-[#205B5A]" : "border-transparent text-[#757675] hover:text-[#1A1B1A]"
              }`}>
              <t.icon className="w-3.5 h-3.5" />
              {t.label}
              {t.count > 0 && <span className="bg-[#1A1B1A]/[0.06] px-1.5 py-0.5 rounded-full text-[9px]">{t.count}</span>}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        {/* Audience Tab */}
        {tab === "audience" && (
          <div className="space-y-4">
            {/* Stats cards */}
            <div className="grid grid-cols-4 gap-3">
              <div className="bg-white rounded-xl border border-[#1A1B1A]/[0.06] p-4">
                <p className="text-[10px] font-medium text-[#757675] uppercase tracking-wider">Total contacts</p>
                <p className="text-2xl font-semibold text-[#1A1B1A] mt-1" style={H}>{subscriberCounts.total}</p>
              </div>
              <div className="bg-white rounded-xl border border-[#1A1B1A]/[0.06] p-4">
                <p className="text-[10px] font-medium text-emerald-600 uppercase tracking-wider">Subscribed</p>
                <p className="text-2xl font-semibold text-emerald-600 mt-1" style={H}>{subscriberCounts.subscribed}</p>
              </div>
              <div className="bg-white rounded-xl border border-[#1A1B1A]/[0.06] p-4">
                <p className="text-[10px] font-medium text-red-500 uppercase tracking-wider">Unsubscribed</p>
                <p className="text-2xl font-semibold text-red-500 mt-1" style={H}>{subscriberCounts.unsubscribed}</p>
              </div>
              <div className="bg-white rounded-xl border border-[#1A1B1A]/[0.06] p-4">
                <p className="text-[10px] font-medium text-amber-600 uppercase tracking-wider">Pending</p>
                <p className="text-2xl font-semibold text-amber-600 mt-1" style={H}>{subscriberCounts.pending}</p>
              </div>
            </div>

            {/* Filter and search */}
            <div className="flex gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#757675]" />
                <input value={audienceSearch} onChange={e => setAudienceSearch(e.target.value)} placeholder="Search by name or email..."
                  data-testid="audience-search"
                  className="w-full pl-9 pr-3 py-2 rounded-xl border border-[#1A1B1A]/10 text-xs focus:outline-none focus:border-[#205B5A]" />
              </div>
              <div className="flex gap-1.5">
                {["", "subscribed", "unsubscribed", "pending"].map(f => (
                  <button key={f || "all"} onClick={() => setAudienceFilter(f)} data-testid={`filter-${f || "all"}`}
                    className={`px-3 py-2 rounded-xl text-[11px] font-medium transition-colors ${
                      audienceFilter === f ? "bg-[#205B5A] text-white" : "bg-[#FAF9F6] text-[#757675] hover:bg-[#1A1B1A]/5"
                    }`}>
                    {f === "" ? "All" : f.charAt(0).toUpperCase() + f.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            {/* Subscriber list */}
            <div className="bg-white rounded-2xl border border-[#1A1B1A]/[0.06] overflow-hidden">
              {loading ? (
                <div className="p-8 text-center text-sm text-[#757675]">Loading...</div>
              ) : subscribers.length === 0 ? (
                <div className="p-8 text-center">
                  <Users className="w-8 h-8 text-[#757675]/20 mx-auto mb-2" />
                  <p className="text-sm text-[#757675]">No subscribers found</p>
                  <p className="text-xs text-[#757675] mt-1">Patients with marketing consent will appear here automatically.</p>
                </div>
              ) : (
                <div className="divide-y divide-[#1A1B1A]/[0.03]">
                  {subscribers.map(sub => (
                    <div key={sub.id} className="px-5 py-3.5 flex items-center gap-4" data-testid={`subscriber-${sub.id}`}>
                      <div className="w-9 h-9 rounded-full bg-[#205B5A]/10 flex items-center justify-center flex-shrink-0">
                        <span className="text-[10px] font-semibold text-[#205B5A]">{sub.first_name?.[0]}{sub.last_name?.[0]}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-[#1A1B1A]">{sub.first_name} {sub.last_name}</p>
                        <p className="text-[10px] text-[#757675]">{sub.email || "No email"}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`text-[9px] font-semibold px-2.5 py-1 rounded-full ${
                          sub.status === "subscribed" ? "bg-emerald-50 text-emerald-600" :
                          sub.status === "unsubscribed" ? "bg-red-50 text-red-600" :
                          "bg-amber-50 text-amber-600"
                        }`}>{sub.status}</span>
                        {sub.status === "subscribed" && !sub.email && (
                          <span className="text-[9px] text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full flex items-center gap-1">
                            <AlertCircle className="w-2.5 h-2.5" /> No email - cannot send
                          </span>
                        )}
                        <div className="flex gap-1">
                          {sub.status !== "subscribed" && sub.email && (
                            <button onClick={() => updateSubscriberStatus(sub.id, "subscribed")}
                              className="text-[10px] text-[#205B5A] hover:underline">Subscribe</button>
                          )}
                          {sub.status !== "unsubscribed" && (
                            <button onClick={() => updateSubscriberStatus(sub.id, "unsubscribed")}
                              className="text-[10px] text-red-500 hover:underline">Unsubscribe</button>
                          )}
                        </div>
                        {sub.patient_id && (
                          <Link to={`/app/patients/${sub.patient_id}`} className="text-[10px] text-[#757675] hover:text-[#205B5A] flex items-center gap-0.5">
                            View patient <ExternalLink className="w-2.5 h-2.5" />
                          </Link>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Campaigns Tab */}
        {tab === "campaigns" && (
          <div className="space-y-4">
            {/* New campaign form */}
            {showNewCampaign && (
              <div className="bg-white rounded-2xl border border-[#1A1B1A]/[0.06] p-5" data-testid="new-campaign-form">
                <p className="text-sm font-medium text-[#1A1B1A] mb-3" style={H}>New campaign</p>
                <div className="space-y-3">
                  <input value={newCampaignForm.title} onChange={e => setNewCampaignForm({...newCampaignForm, title: e.target.value})}
                    placeholder="Campaign name (internal)" data-testid="campaign-title-input"
                    className="w-full px-3 py-2.5 rounded-xl border border-[#1A1B1A]/10 text-sm focus:outline-none focus:border-[#205B5A]" />
                  <input value={newCampaignForm.subject} onChange={e => setNewCampaignForm({...newCampaignForm, subject: e.target.value})}
                    placeholder="Email subject line" data-testid="campaign-subject-input"
                    className="w-full px-3 py-2.5 rounded-xl border border-[#1A1B1A]/10 text-sm focus:outline-none focus:border-[#205B5A]" />
                  <textarea value={newCampaignForm.body} onChange={e => setNewCampaignForm({...newCampaignForm, body: e.target.value})}
                    placeholder="Email content (plain text or HTML)" rows={6} data-testid="campaign-body-input"
                    className="w-full px-3 py-2.5 rounded-xl border border-[#1A1B1A]/10 text-sm focus:outline-none focus:border-[#205B5A] resize-none" />
                  <div className="flex gap-2">
                    <button onClick={createCampaign} data-testid="save-campaign-btn"
                      className="bg-[#205B5A] text-white px-4 py-2 rounded-full text-xs font-medium hover:bg-[#174544]">Create campaign</button>
                    <button onClick={() => setShowNewCampaign(false)} className="text-xs text-[#757675]">Cancel</button>
                  </div>
                </div>
              </div>
            )}

            {/* Edit campaign form */}
            {editingCampaign && (
              <div className="bg-white rounded-2xl border border-[#1A1B1A]/[0.06] p-5" data-testid="edit-campaign-form">
                <p className="text-sm font-medium text-[#1A1B1A] mb-3" style={H}>Edit campaign</p>
                <div className="space-y-3">
                  <input value={editingCampaign.title} onChange={e => setEditingCampaign({...editingCampaign, title: e.target.value})}
                    className="w-full px-3 py-2.5 rounded-xl border border-[#1A1B1A]/10 text-sm focus:outline-none focus:border-[#205B5A]" />
                  <input value={editingCampaign.subject} onChange={e => setEditingCampaign({...editingCampaign, subject: e.target.value})}
                    className="w-full px-3 py-2.5 rounded-xl border border-[#1A1B1A]/10 text-sm focus:outline-none focus:border-[#205B5A]" />
                  <textarea value={editingCampaign.body} onChange={e => setEditingCampaign({...editingCampaign, body: e.target.value})}
                    rows={6} className="w-full px-3 py-2.5 rounded-xl border border-[#1A1B1A]/10 text-sm focus:outline-none focus:border-[#205B5A] resize-none" />
                  <div className="flex gap-2">
                    <button onClick={updateCampaign} className="bg-[#205B5A] text-white px-4 py-2 rounded-full text-xs font-medium hover:bg-[#174544]">Save changes</button>
                    <button onClick={() => setEditingCampaign(null)} className="text-xs text-[#757675]">Cancel</button>
                  </div>
                </div>
              </div>
            )}

            {/* Campaigns list */}
            <div className="bg-white rounded-2xl border border-[#1A1B1A]/[0.06] overflow-hidden">
              {campaigns.length === 0 ? (
                <div className="p-8 text-center">
                  <Mail className="w-8 h-8 text-[#757675]/20 mx-auto mb-2" />
                  <p className="text-sm text-[#757675]">No campaigns yet</p>
                  <p className="text-xs text-[#757675] mt-1">Create a campaign to reach your subscribed patients.</p>
                </div>
              ) : (
                <div className="divide-y divide-[#1A1B1A]/[0.03]">
                  {campaigns.map(c => (
                    <div key={c.id} className="px-5 py-4 flex items-center gap-4" data-testid={`campaign-${c.id}`}>
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${CAMPAIGN_STATUS[c.status]?.iconBg || "bg-amber-50"}`}>
                        {c.status === "sent" ? <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                          : c.status === "failed" ? <AlertCircle className="w-5 h-5 text-red-500" />
                          : <Mail className={`w-5 h-5 ${c.status === "partially_sent" ? "text-amber-600" : "text-amber-600"}`} />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-[#1A1B1A]">{c.title}</p>
                        <p className="text-[10px] text-[#757675] truncate">Subject: {c.subject}</p>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <span
                            className={`text-[9px] font-semibold px-2 py-0.5 rounded-full ${CAMPAIGN_STATUS[c.status]?.badge || "bg-amber-50 text-amber-600"}`}
                            data-testid={`campaign-status-${c.id}`}
                          >
                            {CAMPAIGN_STATUS[c.status]?.label || "Draft"}
                          </span>
                          {(c.sent_count > 0 || c.failed_count > 0 || c.skipped_count > 0 || c.queued_count > 0) && (
                            <span className="text-[10px] text-[#757675]" data-testid={`campaign-counts-${c.id}`}>
                              {c.sent_count || 0} sent
                              {c.failed_count ? ` · ${c.failed_count} failed` : ""}
                              {c.queued_count ? ` · ${c.queued_count} queued` : ""}
                              {c.skipped_count ? ` · ${c.skipped_count} skipped` : ""}
                            </span>
                          )}
                          {c.sent_at && <span className="text-[10px] text-[#757675]">{c.sent_at.split("T")[0]}</span>}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {c.status === "draft" && (
                          <>
                            <button onClick={() => setEditingCampaign({...c})} data-testid={`edit-campaign-${c.id}`}
                              className="p-1.5 rounded-lg hover:bg-[#FAF9F6] text-[#757675]"><Pencil className="w-4 h-4" /></button>
                            <button onClick={() => deleteCampaign(c.id)} data-testid={`delete-campaign-${c.id}`}
                              className="p-1.5 rounded-lg hover:bg-red-50 text-[#757675] hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
                            <button onClick={() => previewAndSend(c)} data-testid={`preview-campaign-${c.id}`}
                              className="bg-[#205B5A] text-white px-3 py-1.5 rounded-lg text-[11px] font-medium hover:bg-[#174544] flex items-center gap-1">
                              <Eye className="w-3.5 h-3.5" /> Preview & send
                            </button>
                          </>
                        )}
                        {(c.status === "partially_sent" || c.status === "failed" || c.status === "sending") && (
                          <button onClick={() => retryCampaign(c.id)} disabled={sending} data-testid={`retry-campaign-${c.id}`}
                            className="border border-[#205B5A]/30 text-[#205B5A] px-3 py-1.5 rounded-lg text-[11px] font-medium hover:bg-[#205B5A]/5 disabled:opacity-50">
                            Retry delivery
                          </button>
                        )}
                        {c.status !== "draft" && (
                          <Link to="#" className="text-[10px] text-[#757675] hover:text-[#205B5A]">View details</Link>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Sent History Tab */}
        {tab === "sent" && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-white rounded-xl border border-[#1A1B1A]/[0.06] p-4">
                <p className="text-[10px] font-medium text-[#757675] uppercase tracking-wider">Total emails sent</p>
                <p className="text-2xl font-semibold text-[#1A1B1A] mt-1" style={H}>{stats.total_emails_sent}</p>
              </div>
              <div className="bg-white rounded-xl border border-[#1A1B1A]/[0.06] p-4">
                <p className="text-[10px] font-medium text-[#757675] uppercase tracking-wider">Campaigns sent</p>
                <p className="text-2xl font-semibold text-[#1A1B1A] mt-1" style={H}>{stats.total_campaigns_sent}</p>
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-[#1A1B1A]/[0.06] overflow-hidden">
              <div className="px-5 py-3 border-b border-[#1A1B1A]/[0.03]">
                <p className="text-xs font-medium text-[#1A1B1A]">Recent campaigns</p>
              </div>
              {stats.recent_campaigns.length === 0 ? (
                <div className="p-8 text-center text-sm text-[#757675]">No campaigns sent yet</div>
              ) : (
                <div className="divide-y divide-[#1A1B1A]/[0.03]">
                  {stats.recent_campaigns.map(c => (
                    <div key={c.id} className="px-5 py-3.5 flex items-center gap-4">
                      {c.status === "sent"
                        ? <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                        : <AlertCircle className={`w-4 h-4 ${c.status === "partially_sent" ? "text-amber-500" : "text-red-500"}`} />}
                      <div className="flex-1">
                        <p className="text-sm font-medium text-[#1A1B1A]">{c.title}</p>
                        <p className="text-[10px] text-[#757675]">
                          {CAMPAIGN_STATUS[c.status]?.label || c.status}
                          {c.sent_at ? ` · ${c.sent_at.split("T")[0]}` : ""}
                          {` · ${c.sent_count || 0} sent`}
                          {c.failed_count ? ` · ${c.failed_count} failed` : ""}
                          {c.skipped_count ? ` · ${c.skipped_count} skipped` : ""}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="bg-white rounded-2xl border border-[#1A1B1A]/[0.06] overflow-hidden">
              <div className="px-5 py-3 border-b border-[#1A1B1A]/[0.03]">
                <p className="text-xs font-medium text-[#1A1B1A]">Email delivery log</p>
                <p className="text-[10px] text-[#757675] mt-0.5">Every recipient of every campaign, including skipped and failed sends.</p>
              </div>
              {sentHistory.length === 0 ? (
                <div className="p-8 text-center text-sm text-[#757675]" data-testid="delivery-log-empty">No campaign emails yet</div>
              ) : (
                <div className="divide-y divide-[#1A1B1A]/[0.03]" data-testid="delivery-log">
                  {sentHistory.map(s => (
                    <div key={s.id} className="px-5 py-3 flex items-start gap-3" data-testid={`delivery-row-${s.id}`}>
                      <span className={`text-[9px] font-semibold px-2 py-0.5 rounded-full mt-0.5 flex-shrink-0 ${
                        s.status === "sent" ? "bg-emerald-50 text-emerald-600"
                        : s.status === "failed" ? "bg-red-50 text-red-600"
                        : s.status === "skipped" ? "bg-[#FAF9F6] text-[#757675]"
                        : s.status === "queued" ? "bg-blue-50 text-blue-600"
                        : "bg-amber-50 text-amber-600"
                      }`}>{s.status}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-[#1A1B1A] truncate">
                          {s.recipient_name || "Unknown"} · <span className="text-[#757675]">{s.email || "no email"}</span>
                        </p>
                        <p className="text-[10px] text-[#757675] truncate">{s.subject}</p>
                        {s.provider_message_id && (
                          <p className="text-[10px] text-[#9A9B9A] font-mono truncate">ID {s.provider_message_id}</p>
                        )}
                        {s.status === "queued" ? (
                          <p className="text-[10px] text-blue-600" data-testid={`queued-reason-${s.id}`}>
                            {s.failure_reason === "never_delivered_before_fix"
                              ? "Never delivered — this campaign was created before sending was fixed. Use Retry delivery."
                              : "Waiting to send — the email provider is rate limiting. Use Retry delivery."}
                          </p>
                        ) : s.skip_reason ? (
                          <p className="text-[10px] text-[#757675]" data-testid={`skip-reason-${s.id}`}>
                            Not sent — {SKIP_LABELS[s.skip_reason] || s.skip_reason}
                          </p>
                        ) : s.failure_reason ? (
                          <p className="text-[10px] text-red-500">Delivery failed — {s.failure_reason}</p>
                        ) : null}
                      </div>
                      <span className="text-[10px] text-[#757675] flex-shrink-0">
                        {(s.sent_at || s.created_at || "").split("T")[0]}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Preview Modal */}
      {previewCampaign && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="preview-modal">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-[#1A1B1A]/[0.06] flex items-center justify-between">
              <div>
                <h3 className="text-base font-medium text-[#1A1B1A]" style={H}>Campaign preview</h3>
                <p className="text-[10px] text-[#757675]">
                  {previewCampaign.recipient_count} eligible recipients
                  {previewCampaign.skipped_count ? ` · ${previewCampaign.skipped_count} will be skipped (no consent, unsubscribed or invalid email)` : ""}
                </p>
              </div>
              <button onClick={() => setPreviewCampaign(null)} className="p-1 text-[#757675] hover:text-[#1A1B1A]"><X className="w-4 h-4" /></button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              {/* Email preview */}
              <div className="border border-[#1A1B1A]/10 rounded-xl overflow-hidden">
                <div className="bg-[#FAF9F6] px-4 py-2 border-b border-[#1A1B1A]/[0.04]">
                  <p className="text-xs text-[#757675]">Subject: <span className="font-medium text-[#1A1B1A]">{previewCampaign.campaign.subject}</span></p>
                </div>
                <div className="p-4 text-sm text-[#1A1B1A] whitespace-pre-wrap">{previewCampaign.campaign.body || "(No content)"}</div>
              </div>

              {/* Recipients preview */}
              <div>
                <p className="text-xs font-medium text-[#757675] mb-2">Recipients ({previewCampaign.recipient_count})</p>
                <div className="bg-[#FAF9F6] rounded-xl p-3 max-h-40 overflow-y-auto space-y-1">
                  {previewCampaign.recipients.map(r => (
                    <div key={r.id} className="flex items-center gap-2 text-xs">
                      <span className="text-[#1A1B1A]">{r.first_name} {r.last_name}</span>
                      <span className="text-[#757675]">{r.email}</span>
                    </div>
                  ))}
                  {previewCampaign.has_more && (
                    <p className="text-[10px] text-[#757675] pt-1">+ {previewCampaign.recipient_count - 50} more...</p>
                  )}
                </div>
              </div>

              {/* Consent notice */}
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3 flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-emerald-700">All recipients have accepted marketing consent. Only subscribed patients with valid email addresses will receive this campaign.</p>
              </div>
            </div>

            <div className="px-5 py-4 border-t border-[#1A1B1A]/[0.06] flex justify-end gap-2">
              <button onClick={() => setPreviewCampaign(null)} className="px-4 py-2 text-xs text-[#757675]">Cancel</button>
              <button onClick={() => setSendConfirm(previewCampaign)} data-testid="confirm-send-btn"
                className="bg-[#205B5A] text-white px-4 py-2 rounded-lg text-xs font-medium hover:bg-[#174544] flex items-center gap-1.5">
                <Send className="w-3.5 h-3.5" /> Send to {previewCampaign.recipient_count} recipients
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Send Confirmation Modal */}
      {sendConfirm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50" data-testid="send-confirm-modal">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-5" onClick={e => e.stopPropagation()}>
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-amber-50 flex items-center justify-center flex-shrink-0">
                <AlertCircle className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h3 className="text-base font-medium text-[#1A1B1A]" style={H}>Confirm send</h3>
                <p className="text-xs text-[#757675] mt-1">
                  This will send "{sendConfirm.campaign.subject}" to <strong>{sendConfirm.recipient_count}</strong> subscribed patients immediately.
                </p>
              </div>
            </div>
            <div className="bg-[#FAF9F6] rounded-lg px-3 py-2 text-[10px] text-[#757675] mb-4">
              This action cannot be undone. Only patients with accepted marketing consent will receive this email.
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setSendConfirm(null)} disabled={sending} className="px-4 py-2 text-xs text-[#757675]">Cancel</button>
              <button onClick={confirmSend} disabled={sending} data-testid="final-send-btn"
                className="bg-[#205B5A] text-white px-4 py-2 rounded-lg text-xs font-medium hover:bg-[#174544] disabled:opacity-50 flex items-center gap-1.5">
                {sending ? "Sending..." : <><Send className="w-3.5 h-3.5" /> Send now</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Send Result Modal - always shows a clear outcome */}
      {sendResult && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50" data-testid="send-result-modal">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-5">
            <div className="flex items-start gap-3 mb-4">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                sendResult.status === "sent" ? "bg-emerald-50"
                : sendResult.status === "sending" ? "bg-blue-50"
                : sendResult.status === "partially_sent" ? "bg-amber-50" : "bg-red-50"
              }`}>
                {sendResult.status === "sent"
                  ? <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                  : sendResult.status === "sending"
                  ? <Clock className="w-5 h-5 text-blue-600" />
                  : <AlertCircle className={`w-5 h-5 ${sendResult.status === "partially_sent" ? "text-amber-600" : "text-red-500"}`} />}
              </div>
              <div>
                <h3 className="text-base font-medium text-[#1A1B1A]" style={H}>
                  {sendResult.status === "sent" ? "Campaign sent"
                    : sendResult.status === "sending" ? "Campaign sending"
                    : sendResult.status === "partially_sent" ? "Campaign partially sent"
                    : "Campaign not sent"}
                </h3>
                <p className="text-xs text-[#757675] mt-1" data-testid="send-result-message">{sendResult.message}</p>
              </div>
            </div>
            {sendResult.sent !== undefined && (
              <div className="bg-[#FAF9F6] rounded-lg px-3 py-2 text-[10px] text-[#757675] mb-4" data-testid="send-result-counts">
                {sendResult.sent || 0} sent · {sendResult.failed || 0} failed
                {sendResult.queued ? ` · ${sendResult.queued} queued for retry` : ""} · {sendResult.skipped || 0} skipped.
                Every recipient has a delivery entry below.
              </div>
            )}
            <div className="flex justify-end">
              <button onClick={() => setSendResult(null)} data-testid="send-result-close"
                className="bg-[#205B5A] text-white px-4 py-2 rounded-lg text-xs font-medium hover:bg-[#174544]">
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
